<i18n>
{
  "fr": {
    "createCustomerProjectButton": {
      "authModalTitle": "Connectez-vous pour enregistrer votre projet",
      "button": {
        "label": "Enregistrer mon projet",
        "error": "Une erreur s'est produite pendant la création de votre projet. Veuillez réessayer ou nous contacter.",
        "success": "Le gobelet a bien été sauvegardé en tant que projet."
      }
    }
  }
}
</i18n>

<script>
import axios from 'axios';
import { requestToken } from '../../api/api';
import AuthModal from '../Modals/AuthModal.vue';
import CustomerProjectModal from '../Modals/CustomerProjectModal.vue';
import WithAuthModal from '../../mixins/WithAuthModal';

export default {
  name: 'CreateCustomerProjectButton',
  components: { AuthModal, CustomerProjectModal },
  mixins: [WithAuthModal],
  props: {
    accountUrl: {
      type: String,
      required: true,
    },
    apiUrl: {
      type: String,
      required: true,
    },
    authUrl: {
      type: String,
      required: true,
    },
    customerProjectId: {
      type: Number,
      default: null,
    },
    customClasses: {
      type: String,
      default: null,
    },
    isAuthenticated: Boolean,
  },
  data() {
    return {
      isSaved: null !== this.customerProjectId,
      isSaving: false,
      needsAuthentication: !this.isAuthenticated,
      needsAuthModal: false,
    };
  },
  computed: {
    /**
     * @returns {Array}
     */
    buttonClasses() {
      return [
        this.customClasses,
        'create-customer-project-button__button',
        {
          'create-customer-project-button__button--saved': this.isSaved,
          'create-customer-project-button__button--saving': this.isSaving,
        },
      ];
    },
  },
  methods: {
    createProject() {
      if (this.needsAuthentication) {
        this.needsAuthModal = true;
        this.showAuthModal();
      } else {
        this.callApi();
      }
    },
    async callApi() {
      if (this.needsAuthentication || this.isSaving) {
        return false;
      }

      this.isSaving = true;

      try {
        const response = await axios.post(this.apiUrl, {
          headers: {
            'X-CSRF-TOKEN': await requestToken(),
            Accept: 'application/json',
          },
        });

        this.isSaved = 200 === response.status;

        if (this.isSaved) {
          window.modalStateObservable.isCustomerProjectModalActive = true;
        } else {
          window.flashMessages = [
            {
              type: 'alert-error',
              message: this.$t('createCustomerProjectButton.button.error'),
            },
          ];
          window.addToastMessages();
        }
      } catch (e) {
        window.flashMessages = [
          {
            type: 'alert-error',
            message: this.$t('createCustomerProjectButton.button.error'),
          },
        ];
        window.addToastMessages();
      } finally {
        this.isSaving = false;
      }
    },
    authCallback() {
      this.needsAuthentication = false;
      this.callApi();
    },
  },
};
</script>

<template>
  <div class="create-customer-project-button">
    <button
      type="button"
      class="create-customer-project-button__container"
      :disabled="isSaved || isSaving"
      @click="createProject"
    >
      <span :class="buttonClasses">
        <i
          :class="`icon icon--heart${isSaved ? '-fill' : ''}
          create-customer-project-button__icon`"
        ></i>
      </span>
      <span
        class="create-customer-project-button__label"
        v-text="$t('createCustomerProjectButton.button.label')"
      ></span>
    </button>

    <Portal append to="portal-container--app-footer">
      <template v-if="needsAuthentication">
        <AuthModal
          v-if="needsAuthModal"
          :callback="authCallback"
          :iframe-url="authUrl"
          :title="$t('createCustomerProjectButton.authModalTitle')"
        />
      </template>
      <CustomerProjectModal v-else :account-url="accountUrl" />
    </Portal>
  </div>
</template>

<style scoped lang="scss">
.create-customer-project-button {
  align-content: center;

  @include breakpoint($breakpoint-l) {
    flex-direction: row;
    gap: 2.5em;
  }

  &__container {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    border: none;
    background: none;
    padding: 0;
    cursor: pointer;
    gap: 0.3em;

    &:hover {
      -webkit-text-stroke-width: 1px;
      -webkit-text-stroke-color: $color-red-amaranth;
    }
  }

  &__icon {
    font-size: 1.35419em;
    color: $color-red-amaranth;
  }

  &__label {
    color: $color-red-amaranth;
    text-decoration: underline;
    font-weight: normal;
    margin-bottom: 0.2em;
    line-height: 1.2;

    @include breakpoint($breakpoint-l) {
      font-size: 1.125rem;
    }
  }
}
</style>
